import { Column, View } from '@/styles/reactnative';
import { useCiTradeWebSocketStore } from '@app/hooks/ciTradeWebsocketStore.ts';
import throttle from 'lodash/throttle';
import React, { useEffect, useMemo, useState } from 'react';
import useTradeStore from '../../../../hooks/trade/tradeStore';
import AsksSection from './AsksSection';
import BidsSection from './BidsSection';
import OrderBookCurrentPrice from './OrderBookCurrentPrice';
import OrderBookHeader from './OrderBookHeader';
import OrderBookSkeleton from './OrderBookSkeleton';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

const ORDERBOOK_HEIGHTS = {
  HEADER: 11,
  CURRENT_PRICE: 36,
  ROW: 20,
  ROW_GAP: 2,
};

// 가격 그룹핑 함수
const groupByTickSize = (
  orders: [string, string][],
  tickSize: number,
  isAsk: boolean
): [string, string][] => {
  const grouped = new Map<number, number>();

  orders.forEach(([priceStr, quantityStr]) => {
    const price = parseFloat(priceStr);
    const quantity = parseFloat(quantityStr);

    // Ask는 올림, Bid는 내림으로 그룹핑
    const groupedPrice = isAsk
      ? Math.ceil(price / tickSize) * tickSize
      : Math.floor(price / tickSize) * tickSize;

    const currentQty = grouped.get(groupedPrice) || 0;
    grouped.set(groupedPrice, currentQty + quantity);
  });

  // 정렬: Ask는 오름차순, Bid는 내림차순
  const sorted = Array.from(grouped.entries()).sort((a, b) =>
    isAsk ? a[0] - b[0] : b[0] - a[0]
  );

  return sorted.map(([price, quantity]) => [
    price.toString(),
    quantity.toString(),
  ]);
};

const OrderBook = ({
  filterTab,
  containerHeight = 0,
  selected,
  setOrderBookValue,
  tickSize,
  tickSizeOptions,
}: any) => {
  const { data: tradeStore } = useTradeStore();
  const depthAsks = useCiTradeWebSocketStore((s) => s.depthAsks);
  const depthBids = useCiTradeWebSocketStore((s) => s.depthBids);
  const tradePrice = useCiTradeWebSocketStore((s) => s.tradePrice);

  const [throttledLong, setThrottledLong] = useState<any[]>([]);
  const [throttledShort, setThrottledShort] = useState<any[]>([]);
  const [max, setMax] = useState<number>(0);

  const visibleRowCount = useMemo(() => {
    if (containerHeight <= 0) return { asks: 10, bids: 10 };

    const { HEADER, CURRENT_PRICE, ROW, ROW_GAP } = ORDERBOOK_HEIGHTS;
    const rowWithGap = ROW + ROW_GAP;

    if (filterTab === 'one') {
      // 양쪽 모드: Header + CurrentPrice + Asks + Bids
      const availableHeight = containerHeight - HEADER - CURRENT_PRICE;
      const rowsPerSide = Math.max(
        1,
        Math.floor((availableHeight / 2 + ROW_GAP) / rowWithGap)
      );
      return { asks: rowsPerSide, bids: rowsPerSide };
    } else {
      // 한쪽만 모드: Header + 전체
      const availableHeight = containerHeight - HEADER;
      const rows = Math.max(
        1,
        Math.floor((availableHeight + ROW_GAP) / rowWithGap)
      );
      return { asks: rows, bids: rows };
    }
  }, [containerHeight, filterTab]);

  const formattedOrderBook = useMemo(() => {
    if (!depthAsks || !depthBids || !tradeStore?.pairs) return null;

    const rawSymbol = tradeStore?.pairs.replace('/', '').toLowerCase();
    const isPepe = rawSymbol === 'pepeusdt';

    // 기본 tick size인지 확인 (그룹핑 필요 여부)
    const baseTickSize = tickSizeOptions[0];
    const needsGrouping = tickSize > baseTickSize;

    // 그룹핑 적용
    const groupedBids = needsGrouping
      ? groupByTickSize(depthBids, tickSize, false)
      : depthBids;
    const groupedAsks = needsGrouping
      ? groupByTickSize(depthAsks, tickSize, true)
      : depthAsks;

    const formatSide = (side: any[]) => {
      let runningSum = 0;
      let localMax = 0;

      const formatted = side.map(
        ([priceStr, quantityStr]: [string, string]) => {
          let price = parseFloat(priceStr);
          let quantity = parseFloat(quantityStr);

          if (isPepe) {
            price *= 1000;
            quantity /= 1e7;
          }

          const sum = price * quantity;
          runningSum += sum;
          localMax = Math.max(localMax, runningSum);

          return {
            price,
            amount: quantity,
            sumAmount: runningSum,
          };
        }
      );

      return { formatted, max: localMax };
    };

    const bids = formatSide(groupedBids);
    const asks = formatSide([...groupedAsks].reverse());

    return {
      long: bids.formatted,
      short: asks.formatted,
      max: Math.max(bids.max, asks.max),
    };
  }, [depthAsks, depthBids, tradeStore?.pairs, tickSize, tickSizeOptions]);

  const throttledUpdate = useMemo(
    () =>
      throttle((newData: any) => {
        setThrottledLong(newData?.long);
        setThrottledShort(newData?.short);
        setMax(newData?.max);
      }, 1000),
    []
  );

  useEffect(() => {
    if (formattedOrderBook) throttledUpdate(formattedOrderBook);
  }, [formattedOrderBook]);

  const slicedData = useMemo(() => {
    const slicedAsks = throttledShort.slice(-visibleRowCount.asks);
    const slicedBids = throttledLong.slice(0, visibleRowCount.bids);
    return { asks: slicedAsks, bids: slicedBids };
  }, [throttledShort, throttledLong, visibleRowCount]);

  const sectionHeight = useMemo(() => {
    const { ROW, ROW_GAP } = ORDERBOOK_HEIGHTS;
    const rows = visibleRowCount.asks;
    return rows * ROW + (rows - 1) * ROW_GAP;
  }, [visibleRowCount.asks]);

  return (
    <OrderBookWrapper>
      <OrderBookHeader />
      <OrderBookContent>
        {filterTab === 'one' ? (
          <View style={{ ...styleSize, justifyContent: 'flex-start' }}>
            <OrderBookSkeleton
              loading={
                !tradePrice || !throttledShort.length || !throttledLong.length
              }
            >
              <AsksSection
                asks={slicedData.asks}
                max={max}
                setOrderBookValue={setOrderBookValue}
              />
              <OrderBookCurrentPrice tradeDataP={tradePrice || '0'} />
              <BidsSection
                bids={slicedData.bids}
                max={max}
                setOrderBookValue={setOrderBookValue}
              />
            </OrderBookSkeleton>
          </View>
        ) : filterTab === 'two' ? (
          <View style={{ ...styleSize }}>
            {throttledLong.length ? (
              <BidsSection
                bids={slicedData.bids}
                max={max}
                selected={selected}
                setOrderBookValue={setOrderBookValue}
              />
            ) : (
              <Column style={{ width: '100%' }}>
                <Skeleton height={300} />
              </Column>
            )}
          </View>
        ) : (
          <View style={{ ...styleSize }}>
            {throttledShort.length ? (
              <AsksSection
                asks={slicedData.asks}
                max={max}
                selected={selected}
                setOrderBookValue={setOrderBookValue}
              />
            ) : (
              <Column style={{ width: '100%' }}>
                <Skeleton height={300} />
              </Column>
            )}
          </View>
        )}
      </OrderBookContent>
    </OrderBookWrapper>
  );
};

const styleSize = {
  width: '100%',
  height: '100%',
};

const OrderBookWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const OrderBookContent = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const SectionWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
`;

export default React.memo(OrderBook);
